import {
  Briefcase, Activity, CheckCircle2, Coins, Cpu, Wallet, HardDrive,
  Database, Network as NetIcon, Users, Timer, DollarSign, Zap, Server, Plug,
  Globe, BarChart3, PieChart as PieIcon, LineChart as LineIcon, Layers, FileText,
  Gauge,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";
import type { ReactElement } from "react";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { GlassPanel } from "@/components/dashboard/GlassPanel";
import { ClientOnly } from "@/components/dashboard/ClientOnly";
import { tooltipStyle, axisTick, gridStroke } from "@/components/dashboard/chartStyle";
import { useDashboardLive } from "@/lib/dashboard-live";
import { assignHardwareCostBands, hardwareCostBandLabel } from "@/lib/hardware-cost-band";

/* ============== formatters & shared bits ============== */
const fmtUSD = (n: number) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });
const fmtNum = (n: number) => n.toLocaleString("en-US");
const fmtMoney = (n: number, currency = "USD") => {
  const cc = String(currency || "USD").toUpperCase();
  if (cc === "USD") return fmtUSD(n);
  return `${n.toLocaleString("en-US", { maximumFractionDigits: 2 })} ${cc}`;
};

/* ============== widget shape ============== */
export type WidgetSize = "sm" | "md" | "lg" | "xl";
export type WidgetCategory = "Wallet" | "Jobs" | "Compute" | "Marketplace" | "Network" | "Storage" | "Activity" | "Charts" | "Tables";
export type Widget = {
  id: string;
  title: string;
  hint: string;
  category: WidgetCategory;
  defaultSize: WidgetSize;
  icon: typeof Wallet;
  render: () => ReactElement;
};

/* ============== reusable building blocks ============== */
function StateBadge({ state }: { state: string }) {
  const map: Record<string, { c: string; l: string }> = {
    running: { c: "border-[oklch(0.62_0.18_245)]/30 bg-[oklch(0.62_0.18_245)]/10 text-[oklch(0.62_0.18_245)]", l: "Running" },
    completed: { c: "border-[var(--success)]/30 bg-[var(--success)]/10 text-[var(--success)]", l: "Completed" },
    failed: { c: "border-destructive/30 bg-destructive/10 text-destructive", l: "Failed" },
    queued: { c: "border-[var(--warning)]/30 bg-[var(--warning)]/10 text-[var(--warning)]", l: "Queued" },
    connected: { c: "border-[var(--success)]/30 bg-[var(--success)]/10 text-[var(--success)]", l: "Connected" },
    pending: { c: "border-[var(--warning)]/30 bg-[var(--warning)]/10 text-[var(--warning)]", l: "Pending" },
  };
  const key = String(state || "queued").toLowerCase();
  const { c, l } = map[key] ?? map.queued;
  return <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${c}`}>{l}</span>;
}

function MiniStat({ icon: Icon, label, value }: { icon: typeof Cpu; label: string; value: number | string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="rounded-lg border hairline bg-soft p-2 text-primary"><Icon className="h-3.5 w-3.5" /></div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-sm font-semibold">{value}</p>
      </div>
    </div>
  );
}

function EmptyHint({ text = "No live data yet." }: { text?: string }) {
  return <p className="text-xs text-muted-foreground">{text}</p>;
}

function KPI({ label, value, icon, accent, hint }: { label: string; value: string | number; icon: typeof Cpu; accent: "cyan" | "magenta" | "mint" | "amber" | "violet"; hint?: string }) {
  return <MetricCard label={label} value={value} icon={icon} accent={accent} hint={hint} />;
}

/* ============== KPI widgets (sm) ============== */
function KpiTotalJobs() {
  const { metrics } = useDashboardLive();
  return <KPI label="Total Jobs" value={fmtNum(metrics.totalJobs)} icon={Briefcase} accent="cyan" hint="all-time" />;
}

function KpiJobsRunning() {
  const { metrics } = useDashboardLive();
  return <KPI label="Jobs Running" value={metrics.jobsRunning} icon={Activity} accent="magenta" hint={`${metrics.jobsQueued} queued`} />;
}

function KpiSuccessRate() {
  const { metrics } = useDashboardLive();
  return <KPI label="Success Rate" value={`${metrics.successRate.toFixed(2)}%`} icon={CheckCircle2} accent="mint" hint={`${fmtNum(metrics.settledJobs)} settled`} />;
}

function KpiAvgJobCost() {
  const { metrics } = useDashboardLive();
  return <KPI label="Avg Job Cost" value={fmtUSD(metrics.avgCompletedJobCostUSD)} icon={Coins} accent="amber" hint={`${fmtNum(metrics.jobsCompleted)} completed`} />;
}

function KpiJobsCompleted() {
  const { metrics } = useDashboardLive();
  return <KPI label="Jobs Completed" value={fmtNum(metrics.jobsCompleted)} icon={CheckCircle2} accent="mint" />;
}

function KpiJobsFailed() {
  const { metrics } = useDashboardLive();
  return <KPI label="Jobs Failed" value={fmtNum(metrics.jobsFailed)} icon={Activity} accent="magenta" />;
}

function KpiAvgRun() {
  const { metrics } = useDashboardLive();
  return <KPI label="Avg Run" value={`${metrics.avgRunDurationMin.toFixed(1)}m`} icon={Timer} accent="violet" />;
}

function KpiUsageSpend() {
  const { metrics } = useDashboardLive();
  return <KPI label="Usage Spend" value={fmtUSD(metrics.usageSpendUSD)} icon={DollarSign} accent="amber" hint="from job ledger" />;
}

function KpiWalletTotal() {
  const { metrics } = useDashboardLive();
  return <KPI label="Wallet Total" value={fmtUSD(metrics.walletTotalUSD)} icon={Wallet} accent="mint" hint="provider wallets" />;
}

function KpiHubCredits() {
  const { metrics } = useDashboardLive();
  return <KPI label="Hub Credits" value={fmtNum(Math.round(metrics.hubCreditsGNRL))} icon={Coins} accent="amber" hint="GNRL" />;
}

function KpiGpuHours() {
  const { metrics } = useDashboardLive();
  return <KPI label="GPU Hours" value={fmtNum(Math.round(metrics.billedGpuHours))} icon={Cpu} accent="cyan" hint="metered" />;
}

function KpiHardwareCostBands() {
  return <KPI label="Hardware pricing" value="Relative bands" icon={Cpu} accent="mint" hint="exact rates hidden" />;
}

function KpiLiveBids() {
  const { metrics } = useDashboardLive();
  return <KPI label="Live Bids" value={metrics.liveBids} icon={Zap} accent="violet" hint={`${metrics.bidsPerProvider.toFixed(1)}/provider`} />;
}

function KpiProviders() {
  const { metrics } = useDashboardLive();
  return <KPI label="Providers" value={metrics.providersOffered} icon={Server} accent="cyan" hint="with stock" />;
}

function KpiConnections() {
  const { metrics } = useDashboardLive();
  return <KPI label="Hubs" value={metrics.hubCount} icon={Plug} accent="cyan" hint="active on this node" />;
}

function KpiPeers() {
  const { metrics } = useDashboardLive();
  return <KPI label="Active Spokes" value={metrics.activeSpokeCount} icon={Users} accent="violet" hint={`${metrics.liveSpokeCount} with live capacity`} />;
}

function KpiStorage() {
  const { metrics } = useDashboardLive();
  return <KPI label="Storage" value={`${fmtNum(metrics.storageTotalGB)} GB`} icon={HardDrive} accent="mint" />;
}

function KpiVolumes() {
  const { metrics } = useDashboardLive();
  return <KPI label="Volumes" value={metrics.volumeCount} icon={Database} accent="amber" />;
}

const kpi: Widget[] = [
  { id: "kpi.totalJobs", title: "Total Jobs", hint: "All-time submissions", category: "Jobs", defaultSize: "sm", icon: Briefcase, render: () => <KpiTotalJobs /> },
  { id: "kpi.jobsRunning", title: "Jobs Running", hint: "Currently executing", category: "Jobs", defaultSize: "sm", icon: Activity, render: () => <KpiJobsRunning /> },
  { id: "kpi.successRate", title: "Success Rate", hint: "Settled job success %", category: "Jobs", defaultSize: "sm", icon: CheckCircle2, render: () => <KpiSuccessRate /> },
  { id: "kpi.avgJobCost", title: "Avg Job Cost", hint: "USD per completed job", category: "Jobs", defaultSize: "sm", icon: Coins, render: () => <KpiAvgJobCost /> },
  { id: "kpi.jobsCompleted", title: "Jobs Completed", hint: "All-time completed", category: "Jobs", defaultSize: "sm", icon: CheckCircle2, render: () => <KpiJobsCompleted /> },
  { id: "kpi.jobsFailed", title: "Jobs Failed", hint: "All-time failures", category: "Jobs", defaultSize: "sm", icon: Activity, render: () => <KpiJobsFailed /> },
  { id: "kpi.avgRun", title: "Avg Run Time", hint: "Average duration", category: "Jobs", defaultSize: "sm", icon: Timer, render: () => <KpiAvgRun /> },

  { id: "kpi.usageSpend", title: "Usage Spend", hint: "Total billed usage", category: "Wallet", defaultSize: "sm", icon: DollarSign, render: () => <KpiUsageSpend /> },
  { id: "kpi.walletTotal", title: "Wallet Total", hint: "All-account USD equivalent", category: "Wallet", defaultSize: "sm", icon: Wallet, render: () => <KpiWalletTotal /> },
  { id: "kpi.hubCredits", title: "Hub Credits", hint: "GNRL token balance", category: "Wallet", defaultSize: "sm", icon: Coins, render: () => <KpiHubCredits /> },

  { id: "kpi.gpuHours", title: "GPU Hours", hint: "Billed compute hours", category: "Compute", defaultSize: "sm", icon: Cpu, render: () => <KpiGpuHours /> },
  { id: "kpi.usdPerGpuHr", title: "Hardware cost bands", hint: "Relative pricing only", category: "Compute", defaultSize: "sm", icon: Cpu, render: () => <KpiHardwareCostBands /> },

  { id: "kpi.liveBids", title: "Live Bids", hint: "Open marketplace bids", category: "Marketplace", defaultSize: "sm", icon: Zap, render: () => <KpiLiveBids /> },
  { id: "kpi.providers", title: "Providers Offered", hint: "Sources with capacity", category: "Marketplace", defaultSize: "sm", icon: Server, render: () => <KpiProviders /> },

  { id: "kpi.connections", title: "Hubs", hint: "Hub daemons", category: "Network", defaultSize: "sm", icon: Plug, render: () => <KpiConnections /> },
  { id: "kpi.peers", title: "Active Spokes", hint: "ACK-active spoke partners", category: "Network", defaultSize: "sm", icon: Users, render: () => <KpiPeers /> },

  { id: "kpi.storage", title: "Storage Total", hint: "Across regions", category: "Storage", defaultSize: "sm", icon: HardDrive, render: () => <KpiStorage /> },
  { id: "kpi.volumes", title: "Volumes", hint: "Provisioned volumes", category: "Storage", defaultSize: "sm", icon: Database, render: () => <KpiVolumes /> },
];

/* ============== Chart / Panel widgets (md, lg) ============== */
function WalletHeroWidget() {
  const { metrics, walletAccounts } = useDashboardLive();
  return (
    <GlassPanel className="relative h-full overflow-hidden">
      <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-gradient-to-br from-[oklch(0.70_0.18_145/0.35)] to-transparent blur-3xl" />
      <div className="relative">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <Wallet className="h-3.5 w-3.5 text-[var(--success)]" /> Wallet balance
        </div>
        <p className="mt-2 font-display text-4xl font-semibold tracking-tight">{fmtUSD(metrics.walletTotalUSD)}</p>
        <div className="mt-1 flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--success)]/30 bg-[var(--success)]/10 px-2 py-0.5 font-medium text-[var(--success)]">Live</span>
          <span className="text-muted-foreground">USD-denominated balances</span>
        </div>
        <div className="-mx-1 mt-3 h-16">
          <ClientOnly><ResponsiveContainer>
            <BarChart data={walletAccounts.slice(0, 8)} margin={{ top: 2, bottom: 0, left: 0, right: 0 }}>
              <Tooltip {...tooltipStyle} formatter={(v: number) => fmtUSD(v)} />
              <Bar dataKey="credit" fill="oklch(0.70 0.18 145)" radius={[5, 5, 0, 0]} />
            </BarChart>
          </ResponsiveContainer></ClientOnly>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 border-t hairline pt-4">
          <div>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><Coins className="h-3 w-3 text-[var(--warning)]" /> Hub credits</div>
            <p className="mt-1 font-display text-xl font-semibold">{fmtNum(Math.round(metrics.hubCreditsGNRL))}</p>
            <p className="text-[11px] text-muted-foreground">GNRL token</p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground"><DollarSign className="h-3 w-3 text-[var(--warning)]" /> Usage spend</div>
            <p className="mt-1 font-display text-xl font-semibold">{fmtUSD(metrics.usageSpendUSD)}</p>
            <p className="text-[11px] text-muted-foreground">from job billing</p>
          </div>
        </div>
      </div>
    </GlassPanel>
  );
}

function ProviderBidsChartWidget() {
  const { providerLiveRows } = useDashboardLive();
  return (
    <GlassPanel title="Provider bid snapshot" subtitle="Current live offers by provider" className="h-full">
      <div className="h-72">
        {providerLiveRows.length === 0 ? (
          <EmptyHint text="No live provider offers." />
        ) : (
          <ClientOnly><ResponsiveContainer>
            <BarChart data={providerLiveRows} margin={{ left: -10, right: 8, top: 4 }}>
              <CartesianGrid stroke={gridStroke} vertical={false} />
              <XAxis dataKey="provider" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} width={32} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="bids" fill="oklch(0.62 0.18 245)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer></ClientOnly>
        )}
      </div>
    </GlassPanel>
  );
}

function ExecutionDistributionWidget() {
  const { executionRows, metrics } = useDashboardLive();
  return (
    <GlassPanel title="Execution distribution" subtitle={`${fmtNum(metrics.totalJobs)} jobs total`} className="h-full">
      <div className="flex h-72 flex-col">
        {metrics.totalJobs === 0 ? (
          <EmptyHint text="No jobs yet." />
        ) : (
          <>
            <div className="min-h-0 flex-1">
              <ClientOnly><ResponsiveContainer>
                <PieChart margin={{ top: 4, bottom: 4, left: 4, right: 4 }}>
                  <Pie data={executionRows} innerRadius="55%" outerRadius="85%" paddingAngle={3} dataKey="value" stroke="var(--background)" strokeWidth={3}>
                    {executionRows.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                  </Pie>
                  <Tooltip {...tooltipStyle} />
                </PieChart>
              </ResponsiveContainer></ClientOnly>
            </div>
            <Legend payload={executionRows.map((d) => ({ value: d.name, type: "circle", color: d.color, id: d.name }))} wrapperStyle={{ position: "static", paddingTop: 8 }} iconType="circle" />
          </>
        )}
      </div>
    </GlassPanel>
  );
}

function WeeklyActivityWidget() {
  const { weeklyRows, peakDay } = useDashboardLive();
  return (
    <GlassPanel title="Weekly job activity" subtitle={`Peak · ${peakDay.day} (${peakDay.jobs} jobs)`} className="h-full">
      <div className="h-60">
        {weeklyRows.length === 0 ? (
          <EmptyHint text="No recent activity." />
        ) : (
          <ClientOnly><ResponsiveContainer>
            <BarChart data={weeklyRows} margin={{ left: -10, right: 8 }}>
              <defs>
                <linearGradient id="bar-grad-w" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="oklch(0.62 0.18 245)" />
                  <stop offset="100%" stopColor="oklch(0.72 0.20 320)" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={gridStroke} vertical={false} />
              <XAxis dataKey="day" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} width={32} />
              <Tooltip {...tooltipStyle} cursor={{ fill: "var(--soft-fill)" }} />
              <Bar dataKey="jobs" fill="url(#bar-grad-w)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer></ClientOnly>
        )}
      </div>
    </GlassPanel>
  );
}

function MarketplacePanelWidget() {
  const { metrics } = useDashboardLive();
  return (
    <GlassPanel title="Marketplace" subtitle="Live supply & demand" className="h-full">
      <div className="space-y-4">
        <div className="flex items-end justify-between">
          <div><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Live bids</p><p className="font-display text-3xl font-semibold">{metrics.liveBids}</p></div>
          <div className="text-right"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">Providers</p><p className="font-display text-3xl font-semibold">{metrics.providersOffered}</p></div>
        </div>
        <div className="rounded-xl border hairline bg-soft p-3">
          <div className="flex items-center justify-between text-xs"><span className="text-muted-foreground">Bids per provider</span><span className="font-mono font-medium">{metrics.bidsPerProvider.toFixed(1)}</span></div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-soft">
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, (metrics.bidsPerProvider / 20) * 100)}%`, background: "var(--gradient-primary)" }} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 border-t hairline pt-3">
          <MiniStat icon={NetIcon} label="Hubs" value={metrics.hubCount} />
          <MiniStat icon={Users} label="Spokes" value={metrics.activeSpokeCount} />
          <MiniStat icon={HardDrive} label="Storage" value={`${fmtNum(metrics.storageTotalGB)} GB`} />
          <MiniStat icon={Database} label="Volumes" value={metrics.volumeCount} />
        </div>
      </div>
    </GlassPanel>
  );
}

function UsageByProviderChartWidget() {
  const { usageByProviderRows } = useDashboardLive();
  return (
    <GlassPanel title="Usage by provider" subtitle="Billed spend snapshot" className="h-full">
      <div className="h-60">
        {usageByProviderRows.length === 0 ? (
          <EmptyHint text="No billed usage yet." />
        ) : (
          <ClientOnly><ResponsiveContainer>
            <BarChart data={usageByProviderRows} margin={{ left: -10, right: 8 }}>
              <CartesianGrid stroke={gridStroke} vertical={false} />
              <XAxis dataKey="provider" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis tick={axisTick} axisLine={false} tickLine={false} width={32} />
              <Tooltip {...tooltipStyle} formatter={(v: number) => fmtUSD(v)} />
              <Bar dataKey="spend" fill="oklch(0.72 0.20 320)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer></ClientOnly>
        )}
      </div>
    </GlassPanel>
  );
}

function StorageByRegionChartWidget() {
  const { storageByRegionRows, metrics } = useDashboardLive();
  return (
    <GlassPanel title="Storage by region" subtitle={`${fmtNum(metrics.storageTotalGB)} GB total`} className="h-full">
      <div className="h-60">
        {storageByRegionRows.length === 0 ? (
          <EmptyHint text="No provisioned storage volumes." />
        ) : (
          <ClientOnly><ResponsiveContainer>
            <BarChart data={storageByRegionRows} margin={{ left: -10, right: 8 }} layout="vertical">
              <CartesianGrid stroke={gridStroke} horizontal={false} />
              <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="region" tick={axisTick} axisLine={false} tickLine={false} width={80} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="gb" fill="oklch(0.70 0.18 145)" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer></ClientOnly>
        )}
      </div>
    </GlassPanel>
  );
}

const charts: Widget[] = [
  { id: "panel.walletHero", title: "Wallet hero", hint: "Balance, account distribution, credits, spend", category: "Wallet", defaultSize: "md", icon: Wallet, render: () => <WalletHeroWidget /> },
  { id: "chart.providerBids", title: "Provider bids", hint: "Current offers by provider", category: "Charts", defaultSize: "lg", icon: LineIcon, render: () => <ProviderBidsChartWidget /> },
  { id: "chart.execution", title: "Execution distribution", hint: "Job state breakdown", category: "Charts", defaultSize: "md", icon: PieIcon, render: () => <ExecutionDistributionWidget /> },
  { id: "chart.weekly", title: "Weekly job activity", hint: "Last 7 days", category: "Charts", defaultSize: "lg", icon: BarChart3, render: () => <WeeklyActivityWidget /> },
  { id: "panel.marketplace", title: "Marketplace summary", hint: "Live supply & demand snapshot", category: "Marketplace", defaultSize: "md", icon: Gauge, render: () => <MarketplacePanelWidget /> },
  { id: "chart.usageByProvider", title: "Usage by provider", hint: "Spend across providers", category: "Charts", defaultSize: "md", icon: BarChart3, render: () => <UsageByProviderChartWidget /> },
  { id: "chart.storageByRegion", title: "Storage by region", hint: "GB distribution", category: "Storage", defaultSize: "md", icon: Globe, render: () => <StorageByRegionChartWidget /> },
];

/* ============== Tables ============== */
function IntegrationRunsTableWidget() {
  const { integrationRuns, metrics } = useDashboardLive();
  return (
    <GlassPanel
      title="Integration runs"
      subtitle="Recent jobs across all providers"
      className="h-full"
      action={<span className="rounded-full border hairline bg-soft px-3 py-1 text-[11px] text-muted-foreground"><Timer className="mr-1 inline h-3 w-3" /> avg {metrics.avgRunDurationMin.toFixed(1)} min</span>}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b hairline text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-2 py-2.5 text-left font-medium">Job ID</th>
            <th className="px-2 py-2.5 text-left font-medium">State</th>
            <th className="px-2 py-2.5 text-left font-medium">Provider</th>
            <th className="px-2 py-2.5 text-left font-medium">GPU</th>
            <th className="px-2 py-2.5 text-left font-medium">Region</th>
            <th className="px-2 py-2.5 text-right font-medium">Spent</th>
            <th className="px-2 py-2.5 text-right font-medium">Updated</th>
          </tr></thead>
          <tbody>
            {integrationRuns.length === 0 && (
              <tr><td colSpan={7} className="px-2 py-6 text-center text-xs text-muted-foreground">No jobs yet.</td></tr>
            )}
            {integrationRuns.map((r) => (
              <tr key={r.id} className="border-b hairline transition hover:bg-soft">
                <td className="px-2 py-3 font-mono text-xs">{r.id}</td>
                <td className="px-2 py-3"><StateBadge state={r.state} /></td>
                <td className="px-2 py-3">{r.provider}</td>
                <td className="px-2 py-3 font-mono text-xs">{r.gpu}</td>
                <td className="px-2 py-3 text-muted-foreground">{r.region}</td>
                <td className="px-2 py-3 text-right font-mono">{fmtUSD(r.spent)}</td>
                <td className="px-2 py-3 text-right text-xs text-muted-foreground">{r.updated}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassPanel>
  );
}

function PartnersTableWidget() {
  const { partners } = useDashboardLive();
  return (
    <GlassPanel title="Connected partners" subtitle={`${partners.length} partners`} className="h-full">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b hairline text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-2 py-2.5 text-left font-medium">Name</th>
            <th className="px-2 py-2.5 text-left font-medium">Region</th>
            <th className="px-2 py-2.5 text-left font-medium">Availability</th>
            <th className="px-2 py-2.5 text-left font-medium">Status</th>
            <th className="px-2 py-2.5 text-right font-medium">Last check</th>
          </tr></thead>
          <tbody>
            {partners.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-muted-foreground">No partner ACKs yet.</td></tr>
            )}
            {partners.map((p, i) => (
              <tr key={`${p.name}-${i}`} className="border-b hairline transition hover:bg-soft">
                <td className="px-2 py-3 font-medium">{p.name}</td>
                <td className="px-2 py-3 text-muted-foreground">{p.region}</td>
                <td className="px-2 py-3 capitalize">{p.availability}</td>
                <td className="px-2 py-3"><StateBadge state={p.status} /></td>
                <td className="px-2 py-3 text-right text-xs text-muted-foreground">{p.lastChecked}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassPanel>
  );
}

function LiveBidsTableWidget() {
  const { liveBids } = useDashboardLive();
  const costBands = assignHardwareCostBands(liveBids, (bid) => bid.price);
  return (
    <GlassPanel title="Live bids" subtitle={`${liveBids.length} offers`} className="h-full">
      <div className="max-h-[420px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[var(--background)]/80 backdrop-blur"><tr className="border-b hairline text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-2 py-2.5 text-left font-medium">GPU</th>
            <th className="px-2 py-2.5 text-left font-medium">Source</th>
            <th className="px-2 py-2.5 text-left font-medium">Location</th>
            <th className="px-2 py-2.5 text-right font-medium">Capacity</th>
            <th className="px-2 py-2.5 text-right font-medium">Cost band</th>
          </tr></thead>
          <tbody>
            {liveBids.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-muted-foreground">No live offers.</td></tr>
            )}
            {liveBids.map((b, i) => (
              <tr key={`${b.gpu}-${i}`} className="border-b hairline transition hover:bg-soft">
                <td className="px-2 py-2.5 font-mono text-xs">{b.gpu}</td>
                <td className="px-2 py-2.5">{b.source}</td>
                <td className="px-2 py-2.5 text-muted-foreground">{b.location}</td>
                <td className="px-2 py-2.5 text-right font-mono">{b.capacity}</td>
                <td className="px-2 py-2.5 text-right font-medium">{hardwareCostBandLabel(costBands[i] || "Mid cost")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassPanel>
  );
}

function WalletAccountsTableWidget() {
  const { walletAccounts } = useDashboardLive();
  const costBands = assignHardwareCostBands(walletAccounts, (account) => account.bestRate);
  return (
    <GlassPanel title="Wallet accounts" subtitle={`${walletAccounts.length} accounts`} className="h-full">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b hairline text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-2 py-2.5 text-left font-medium">Account</th>
            <th className="px-2 py-2.5 text-left font-medium">Provider</th>
            <th className="px-2 py-2.5 text-right font-medium">Credit</th>
            <th className="px-2 py-2.5 text-right font-medium">Best cost band</th>
            <th className="px-2 py-2.5 text-right font-medium">Updated</th>
          </tr></thead>
          <tbody>
            {walletAccounts.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-muted-foreground">No wallet accounts configured.</td></tr>
            )}
            {walletAccounts.map((a, i) => (
              <tr key={`${a.provider}-${i}`} className="border-b hairline transition hover:bg-soft">
                <td className="px-2 py-3 font-medium">{a.account}</td>
                <td className="px-2 py-3">{a.provider}</td>
                <td className="px-2 py-3 text-right font-mono">{fmtMoney(a.credit, a.currency)}</td>
                <td className="px-2 py-3 text-right font-medium">{hardwareCostBandLabel(costBands[i] || "Mid cost")}</td>
                <td className="px-2 py-3 text-right text-xs text-muted-foreground">{a.updated}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassPanel>
  );
}

function ActivityFeedWidget() {
  const { activityFeed } = useDashboardLive();
  return (
    <GlassPanel title="Activity feed" subtitle="Most recent events" className="h-full">
      <ul className="space-y-2.5">
        {activityFeed.length === 0 && <li className="text-xs text-muted-foreground">No recent activity.</li>}
        {activityFeed.slice(0, 8).map((a, i) => (
          <li key={i} className="flex items-start gap-3 rounded-xl border hairline bg-soft px-3 py-2">
            <span className="mt-0.5 rounded-md border hairline bg-background px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">{a.time}</span>
            <span className="text-xs">{a.label}</span>
          </li>
        ))}
      </ul>
    </GlassPanel>
  );
}

function StorageVolumesTableWidget() {
  const { storageVolumes } = useDashboardLive();
  return (
    <GlassPanel title="Storage volumes" subtitle={`${storageVolumes.length} volumes`} className="h-full">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b hairline text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-2 py-2.5 text-left font-medium">Volume</th>
            <th className="px-2 py-2.5 text-left font-medium">Region</th>
            <th className="px-2 py-2.5 text-left font-medium">Type</th>
            <th className="px-2 py-2.5 text-right font-medium">GB</th>
            <th className="px-2 py-2.5 text-right font-medium">Last write</th>
          </tr></thead>
          <tbody>
            {storageVolumes.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-6 text-center text-xs text-muted-foreground">No live storage volumes.</td></tr>
            )}
            {storageVolumes.map((v) => (
              <tr key={v.id} className="border-b hairline transition hover:bg-soft">
                <td className="px-2 py-3 font-mono text-xs">{v.id}</td>
                <td className="px-2 py-3 text-muted-foreground">{v.region}</td>
                <td className="px-2 py-3">{v.type}</td>
                <td className="px-2 py-3 text-right font-mono">{v.gb}</td>
                <td className="px-2 py-3 text-right text-xs text-muted-foreground">{v.lastWrite}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassPanel>
  );
}

const tables: Widget[] = [
  { id: "table.integrationRuns", title: "Integration runs", hint: "Recent jobs across providers", category: "Tables", defaultSize: "xl", icon: FileText, render: () => <IntegrationRunsTableWidget /> },
  { id: "table.partners", title: "Connected partners", hint: "Live partner status", category: "Tables", defaultSize: "lg", icon: Plug, render: () => <PartnersTableWidget /> },
  { id: "table.bids", title: "Live bids", hint: "GPU offers across the marketplace", category: "Tables", defaultSize: "lg", icon: Zap, render: () => <LiveBidsTableWidget /> },
  { id: "table.walletAccounts", title: "Wallet accounts", hint: "Per-provider balances", category: "Tables", defaultSize: "lg", icon: Wallet, render: () => <WalletAccountsTableWidget /> },
  { id: "panel.activityFeed", title: "Activity feed", hint: "Latest workspace events", category: "Activity", defaultSize: "md", icon: Activity, render: () => <ActivityFeedWidget /> },
  { id: "table.storageVolumes", title: "Storage volumes", hint: "Provisioned volumes", category: "Tables", defaultSize: "lg", icon: HardDrive, render: () => <StorageVolumesTableWidget /> },
];

/* ============== Registry ============== */
export const WIDGETS: Widget[] = [...kpi, ...charts, ...tables];

const byId = new Map(WIDGETS.map((w) => [w.id, w]));
export function getWidget(id: string): Widget | undefined {
  return byId.get(id);
}

/* Default layout — preserves existing dashboard entry points */
export const DEFAULT_LAYOUT: { id: string; size: WidgetSize }[] = [
  { id: "panel.walletHero", size: "md" },
  { id: "kpi.totalJobs", size: "md" },
  { id: "kpi.jobsRunning", size: "sm" },
  { id: "kpi.successRate", size: "sm" },
  { id: "kpi.avgJobCost", size: "sm" },
  { id: "kpi.walletTotal", size: "sm" },
  { id: "chart.providerBids", size: "lg" },
  { id: "chart.execution", size: "md" },
  { id: "chart.weekly", size: "lg" },
  { id: "panel.marketplace", size: "md" },
  { id: "table.integrationRuns", size: "xl" },
];

/* Tailwind grid spans for a 6-col layout. Stays single column on mobile. */
export const SIZE_SPAN: Record<WidgetSize, string> = {
  sm: "lg:col-span-2",
  md: "lg:col-span-3",
  lg: "lg:col-span-4",
  xl: "lg:col-span-6",
};

/* Numeric column widths (out of 6) that match SIZE_SPAN above. */
const SIZE_COLS: Record<WidgetSize, number> = { sm: 2, md: 3, lg: 4, xl: 6 };
const COLS_TO_SIZE: Record<number, WidgetSize> = { 2: "sm", 3: "md", 4: "lg", 6: "xl" };

/**
 * Reflow a layout so each row fills the full 6-column grid with no gaps.
 * This is presentation-only — it never reorders, removes, or adds cards.
 */
export function reflowLayout<T extends { id: string; size: WidgetSize }>(items: T[]): T[] {
  if (!items.length) return items;
  const result: T[] = [];
  let row: T[] = [];
  let rowCols = 0;

  const flushRow = () => {
    if (!row.length) return;
    let leftover = 6 - rowCols;
    while (leftover > 0) {
      let grew = false;
      for (let i = row.length - 1; i >= 0; i--) {
        const cur = SIZE_COLS[row[i].size];
        const candidates = [3, 4, 6].filter((c) => c > cur && c - cur <= leftover);
        if (candidates.length) {
          const next = candidates[0];
          const bump = next - cur;
          row[i] = { ...row[i], size: COLS_TO_SIZE[next] };
          leftover -= bump;
          grew = true;
          break;
        }
      }
      if (!grew) break;
    }
    result.push(...row);
    row = [];
    rowCols = 0;
  };

  for (const item of items) {
    const cols = SIZE_COLS[item.size];
    if (rowCols + cols > 6) flushRow();
    row.push(item);
    rowCols += cols;
    if (rowCols === 6) flushRow();
  }
  flushRow();
  return result;
}
